/**
 * The Yes/No pad for opening-bid modules (feature 023, FR-017a).
 *
 * Every "Opening bid" module asks a single question — "Should you open 1♣?" — so it gets two
 * buttons and NO bid pad. Showing a call pad here would invite the learner to answer a question
 * that was not asked.
 */

export interface YesNoPadProps {
  /** The question, e.g. "Should you open 1♣?" — rendered above the buttons. */
  prompt: React.ReactNode;
  onAnswer: (answer: 'yes' | 'no') => void;
  disabled?: boolean;
  /** Once answered, the chosen option is marked so the feedback has context. */
  chosen?: 'yes' | 'no' | null;
}

// xnarrow/padswap: slimmer buttons so the stacked table-frame panels fit a short or
// ultra-narrow phone's viewport without scrolling. Both variants are needed: padswap misses
// tall-but-320px-wide phones, xnarrow misses ordinary-width short ones (360x640).
const BTN =
  'w-full rounded-lg py-3 text-center text-[15px] font-bold tracking-wide transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 xnarrow:py-2 padswap:py-2';

export function YesNoPad({ prompt, onAnswer, disabled = false, chosen = null }: YesNoPadProps) {
  return (
    <div className="space-y-3 xnarrow:space-y-2 padswap:space-y-2" data-testid="yes-no-pad">
      <p className="text-[15px] font-semibold text-ink" data-testid="yes-no-prompt">
        {prompt}
      </p>

      <button
        type="button"
        disabled={disabled}
        aria-pressed={chosen === 'yes'}
        onClick={() => onAnswer('yes')}
        data-testid="answer-yes"
        className={`${BTN} bg-primary-600 text-white shadow-[0_2px_0_#063d21] hover:bg-primary-700 focus-visible:ring-primary-600 ${
          chosen === 'yes' ? 'ring-2 ring-brass' : ''
        }`}
      >
        Yes
      </button>

      <button
        type="button"
        disabled={disabled}
        aria-pressed={chosen === 'no'}
        onClick={() => onAnswer('no')}
        data-testid="answer-no"
        className={`${BTN} border border-line-strong bg-card text-ink shadow-[0_2px_0_#d8ceae] hover:bg-cream focus-visible:ring-primary-600 ${
          chosen === 'no' ? 'ring-2 ring-brass' : ''
        }`}
      >
        No
      </button>
    </div>
  );
}
